// src/admin/components/services/ServicePaymentDetail.jsx - Internationalized with RTL support
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  RiMoneyDollarCircleLine,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiCalendarLine,
  RiCheckLine,
  RiCloseCircleLine,
  RiSaveLine,
  RiErrorWarningLine,
  RiUserLine,
  RiHome4Line,
  RiInformationLine,
  RiUserStarLine,
  RiBuildingLine,
  RiCurrencyLine,
  RiTimeLine,
  RiPrinterLine
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  updateServicePayment,
  getServicePaymentById,
  formatCurrency,
  formatDate,
  getPaymentStatusColor,
  getMonthName
} from '../../../models/serviceModel';
import PrintServiceReceipt from './PrintServiceReceipt';

const ServicePaymentDetail = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  // Check if the current language is RTL
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  // Select the appropriate arrow icon based on RTL
  const BackArrow = isRTL ? RiArrowRightLine : RiArrowLeftLine;

  const [showPrintModal, setShowPrintModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [payment, setPayment] = useState(null);
  const [saving, setSaving] = useState(false);

  // Currency options
  const currencies = [
    { code: 'USD', symbol: '$', name: t('us_dollar') || 'US Dollar' },
    { code: 'IQD', symbol: 'د.ع', name: t('iraqi_dinar') || 'Iraqi Dinar' },
    { code: 'EUR', symbol: '€', name: t('euro') || 'Euro' }
  ];

  // Form state
  const [formData, setFormData] = useState({
    isPaid: false,
    paymentDate: formatDate(new Date()),
    paidBy: '',
    notes: '',
    currency: 'USD', // Default currency
    amount: ''  // For editing the amount
  });

  // Load payment details
  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getServicePaymentById(id);

        if (response.success) {
          setPayment(response.payment);

          // Initialize form with payment data
          setFormData({
            isPaid: response.payment.isPaid || false,
            paymentDate: response.payment.paymentDate ? new Date(response.payment.paymentDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            paidBy: response.payment.paidBy || '',
            notes: response.payment.notes || '',
            currency: response.payment.currency || 'USD',
            amount: response.payment.amount || 0
          });
        } else {
          throw new Error(response.message || t('failed_to_load_payment_details') || 'Failed to load payment details');
        }
      } catch (err) {
        console.error('Error loading payment details:', err);
        setError(err.message || t('failed_to_load_payment_details') || 'Failed to load payment details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPaymentDetails();
    }
  }, [id, t]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // Clear success/error messages when form changes
    if (success) setSuccess(null);
    if (error) setError(null);
  };

  // Handle currency selection
  const handleCurrencyChange = (currency) => {
    setFormData({
      ...formData,
      currency
    });

    // Clear success/error messages when form changes
    if (success) setSuccess(null);
    if (error) setError(null);
  };


  // Handle print receipt
  const printReceipt = () => {
    if (!payment) return;
    setShowPrintModal(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Prepare update data
      const updateData = {
        isPaid: formData.isPaid,
        paymentDate: formData.isPaid ? formData.paymentDate : null,
        paidBy: formData.isPaid ? formData.paidBy : null,
        notes: formData.notes,
        currency: formData.currency,
        amount: parseFloat(formData.amount)
      };

      const response = await updateServicePayment(id, updateData);

      if (response.success) {
        setSuccess(formData.isPaid
          ? t('payment_marked_as_paid') || 'Payment successfully marked as paid.'
          : t('payment_marked_as_unpaid') || 'Payment successfully marked as unpaid.');

        // Update payment data
        setPayment(response.payment);
      } else {
        throw new Error(response.message || t('failed_to_update_payment') || 'Failed to update payment');
      }
    } catch (err) {
      console.error('Error updating payment:', err);
      setError(err.message || t('failed_to_update_payment') || 'Failed to update payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg`}>
      {/* Header */}
      <div className={`p-5 border-b ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-purple-50'}`}>
        <div className="flex justify-between items-center">
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} flex items-center`}>
            <RiMoneyDollarCircleLine className={`${isRTL ? 'ml-3' : 'mr-3'} text-2xl ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
            {t('service_payment_details') || 'Service Payment Details'}
          </h2>
          <div className={`flex ${isRTL ? 'space-x-2 space-x-reverse' : 'space-x-2'}`}>
            {payment && payment.isPaid && (
              <button
                onClick={printReceipt}
                className={`p-2 rounded-full ${isDarkMode ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-purple-500 text-white hover:bg-purple-600'
                  }`}
                title={t('print_receipt') || 'Print Receipt'}
              >
                <RiPrinterLine size={20} />
              </button>
            )}
            <button
              onClick={() => navigate('/admin/services')}
              className={`p-2 rounded-full ${isDarkMode ? 'bg-zinc-800 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              <BackArrow size={22} />
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="p-5 flex justify-center">
          <div className={`inline-block h-8 w-8 animate-spin rounded-full border-4 ${isDarkMode ? 'border-purple-500 border-t-transparent' : 'border-purple-500 border-t-transparent'
            }`}></div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className={`mx-5 mt-5 p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'} border flex items-center`}>
          <RiErrorWarningLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
          <div>
            <p className="font-medium">{error}</p>
            {error.includes('Backend API endpoint missing') && (
              <p className={`text-sm mt-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                {t('api_endpoint_missing_message') || 'This error indicates a missing API endpoint on your backend server. To fix this, add a route for GET /api/services/:id in your serviceRoutes.js file.'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className={`mx-5 mt-5 p-4 rounded-lg ${isDarkMode ? 'bg-green-900/20 border-green-800 text-green-400' : 'bg-green-50 border-green-200 text-green-700'} border flex items-center`}>
          <RiCheckLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
          {success}
        </div>
      )}

      {!loading && payment && (
        <div className="p-5">
          {/* Payment Details */}
          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'} mb-6`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
              <RiInformationLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('payment_information') || 'Payment Information'}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center mb-3">
                  <RiBuildingLine className={`text-xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} />
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('apartment') || 'Apartment'}</p>
                    <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {payment.apartmentNumber} - {payment.buildingName}
                    </p>
                  </div>
                </div>

                <div className="flex items-center mb-3">
                  <RiCalendarLine className={`text-xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} />
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('period') || 'Period'}</p>
                    <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {t(`month_${payment.month}`) || getMonthName(payment.month)}, {payment.year}
                    </p>
                  </div>
                </div>

                <div className="flex items-center mb-3">
                  <RiCurrencyLine className={`text-xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} />
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('amount') || 'Amount'}</p>
                    <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(payment.amount, payment.currency || 'USD')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center mb-3">
                  <RiCheckLine className={`text-xl ${isRTL ? 'ml-3' : 'mr-3'} ${payment.isPaid ? (isDarkMode ? 'text-green-400' : 'text-green-600') : (isDarkMode ? 'text-red-400' : 'text-red-600')}`} />
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('status') || 'Status'}</p>
                    <p className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getPaymentStatusColor(payment.isPaid, isDarkMode)}`}>
                      {payment.isPaid ? (t('paid') || 'Paid') : (t('unpaid') || 'Unpaid')}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                {payment.tenantName && (
                  <div className="flex items-center mb-3">
                    <RiUserLine className={`text-xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} />
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('tenant') || 'Tenant'}</p>
                      <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {payment.tenantName}
                      </p>
                    </div>
                  </div>
                )}

                {payment.ownerName && (
                  <div className="flex items-center mb-3">
                    <RiUserStarLine className={`text-xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} />
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('owner') || 'Owner'}</p>
                      <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {payment.ownerName}
                      </p>
                    </div>
                  </div>
                )}

                {payment.isPaid && payment.paymentDate && (
                  <div className="flex items-center mb-3">
                    <RiTimeLine className={`text-xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} />
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('payment_date') || 'Payment Date'}</p>
                      <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {new Date(payment.paymentDate).toLocaleDateString(i18n.language)}
                      </p>
                    </div>
                  </div>
                )}

                {payment.paidBy && (
                  <div className="flex items-center mb-3">
                    <RiUserLine className={`text-xl ${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} />
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('paid_by') || 'Paid By'}</p>
                      <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {payment.paidBy === 'tenant' && payment.tenantName ? payment.tenantName :
                          payment.paidBy === 'owner' && payment.ownerName ? payment.ownerName :
                            t(payment.paidBy) || payment.paidBy || t('other') || 'Other'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              {payment.notes && (
                <div className="md:col-span-2 mt-2">
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{t('notes') || 'Notes'}</p>
                  <p className={`mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {payment.notes}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Update Payment Form */}
          <form onSubmit={handleSubmit}>
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'} mb-6`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
                <RiSaveLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('update_payment') || 'Update Payment'}
              </h3>

              <div className="space-y-4">
                {/* Payment Status */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isPaid"
                    name="isPaid"
                    checked={formData.isPaid}
                    onChange={handleInputChange}
                    className={`h-5 w-5 rounded ${isDarkMode ? 'bg-zinc-800 border-gray-600' : 'bg-white border-gray-300'
                      } text-purple-600 focus:ring-purple-500`}
                  />
                  <label
                    htmlFor="isPaid"
                    className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    {t('mark_as_paid') || 'Mark as Paid'}
                  </label>
                </div>

                {/* Amount & Currency */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {t('amount') || 'Amount'} *
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                          } ${formData.currency === 'IQD' ? (isRTL ? 'pr-14' : 'pl-14') : (isRTL ? 'pr-8' : 'pl-8')}`}
                        required
                        min="0.01"
                        step="0.01"
                      />
                      <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none text-${isDarkMode ? 'gray-400' : 'gray-500'
                        }`}>
                        {currencies.find(c => c.code === formData.currency)?.symbol}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {t('currency') || 'Currency'}
                    </label>
                    <div className={`flex ${isRTL ? 'space-x-2 space-x-reverse' : 'space-x-2'}`}>
                      {currencies.map(currency => (
                        <button
                          key={currency.code}
                          type="button"
                          onClick={() => handleCurrencyChange(currency.code)}
                          className={`py-2 px-3 rounded-lg flex items-center ${formData.currency === currency.code
                            ? (isDarkMode ? 'bg-purple-600 text-white' : 'bg-purple-500 text-white')
                            : (isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                            }`}
                        >
                          <span className="text-sm font-medium">{currency.symbol} {currency.code}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Conditional fields (only shown when marked as paid) */}
                {formData.isPaid && (
                  <div className="space-y-4 mt-4 pt-4 border-t border-dashed border-gray-500 border-opacity-30">
                    {/* Payment Date */}
                    <div>
                      <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {t('payment_date') || 'Payment Date'} *
                      </label>
                      <input
                        type="date"
                        name="paymentDate"
                        value={formData.paymentDate}
                        onChange={handleInputChange}
                        className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                        required={formData.isPaid}
                      />
                    </div>

                    {/* Paid By */}
                    <div>
                      <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {t('paid_by') || 'Paid By'}
                      </label>
                      <select
                        name="paidBy"
                        value={formData.paidBy}
                        onChange={handleInputChange}
                        className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                      >
                        <option value="">{t('select_payer') || 'Select Payer'}</option>
                        {payment.tenantName && (
                          <option value="tenant">{payment.tenantName} ({t('tenant') || 'Tenant'})</option>
                        )}
                        {payment.ownerName && (
                          <option value="owner">{payment.ownerName} ({t('owner') || 'Owner'})</option>
                        )}
                        <option value="management">{t('management') || 'Management'}</option>
                        <option value="other">{t('other') || 'Other'}</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('notes') || 'Notes'}
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    placeholder={t('payment_notes_placeholder') || 'Enter any additional notes about this payment...'}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between">
              <button
                type="button"
                onClick={printReceipt}
                disabled={!payment.isPaid}
                className={`px-4 py-2 rounded-lg flex items-center ${payment.isPaid
                  ? (isDarkMode ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-purple-500 text-white hover:bg-purple-600')
                  : (isDarkMode ? 'bg-zinc-800 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-zinc-400 cursor-not-allowed')
                  }`}
              >
                <RiPrinterLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
                {t('print_receipt') || 'Print Receipt'}
              </button>

              <div className={`flex ${isRTL ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
                <button
                  type="button"
                  onClick={() => navigate('/admin/services')}
                  className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
                  disabled={saving}
                >
                  <BackArrow className={`${isRTL ? 'ml-1' : 'mr-1'} inline`} size={18} />
                  {t('cancel') || 'Cancel'}
                </button>

                <button
                  type="submit"
                  className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white transition-colors flex items-center`}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className={`h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                      {t('saving') || 'Saving...'}
                    </>
                  ) : (
                    <>
                      <RiSaveLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
                      {t('update_payment') || 'Update Payment'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>

          {/* Print Receipt Modal */}
          {showPrintModal && payment && (
            <PrintServiceReceipt
              payment={payment}
              onClose={() => setShowPrintModal(false)}
            />
          )}

          {/* Payment History (Optional) */}
          {payment.paymentHistory && payment.paymentHistory.length > 0 && (
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'} mt-6`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
                <RiTimeLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('payment_history') || 'Payment History'}
              </h3>

              <div className="overflow-x-auto">
                <table className={`min-w-full ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <thead className={isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                    <tr>
                      <th className={`px-3 py-3 ${isRTL ? 'text-right' : 'text-left'}`}>{t('date') || 'Date'}</th>
                      <th className={`px-3 py-3 ${isRTL ? 'text-right' : 'text-left'}`}>{t('action') || 'Action'}</th>
                      <th className={`px-3 py-3 ${isRTL ? 'text-right' : 'text-left'}`}>{t('by') || 'By'}</th>
                      <th className={`px-3 py-3 ${isRTL ? 'text-right' : 'text-left'}`}>{t('notes') || 'Notes'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payment.paymentHistory.map((history, index) => (
                      <tr
                        key={index}
                        className={`${isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'} hover:bg-opacity-10 hover:bg-purple-500`}
                      >
                        <td className="px-3 py-3">
                          {new Date(history.date).toLocaleDateString(i18n.language)}
                        </td>
                        <td className="px-3 py-3">
                          {t(history.action) || history.action}
                        </td>
                        <td className="px-3 py-3">
                          {history.by}
                        </td>
                        <td className="px-3 py-3">
                          {history.notes}
                        </td>
                      </tr>
                    ))}


                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ServicePaymentDetail;